<template>
  <section class="landing">
    <!-- ?prelander=1: "Your game is loading up" with the price, then this page on its own (a straight swap,
         as in the design). -->
    <PreLander v-if="prelanding" />
    <div v-else class="landing__page">
    <div class="landing__spacer" aria-hidden="true"></div>

    <!-- Brand art between the header and the copy (Morabaraba): the banner on JOIN, the crest on the
         typed-number steps. Decorative: the logo in the header is the brand mark Evina reads. -->
    <div v-if="hero" class="hero" :class="{ 'hero--compact': typedNumber }" aria-hidden="true">
      <img class="hero__img" :src="hero" alt="" />
    </div>

    <!-- Copy block: eyebrow · headline · subline (VS Gaming leads with the prize). -->
    <div class="copy" :class="`copy--${brand}`">
      <PrizeRow v-if="variant.prize && variant.prize.first" :prize="variant.prize" :compact="typedNumber" />
      <p class="copy__eyebrow" :class="{ 'copy__eyebrow--chip': variant.eyebrowChip }">{{ variant.eyebrow }}</p>
      <PrizeRow v-if="variant.prize && !variant.prize.first" :prize="variant.prize" :compact="typedNumber" />
      <h1 class="copy__headline" :style="headlineStyle">{{ variant.headline }}</h1>
      <!-- The typed-number steps replace the subline with their instruction (SubscribePanel). -->
      <p v-if="subline && !typedNumber" class="copy__subline">{{ subline }}</p>
    </div>

    <ul v-if="chips.length && !typedNumber" class="chips">
      <li v-for="(chip, i) in chips" :key="chip" class="chips__chip" :class="{ 'chips__chip--lead': i === 0 }">{{ chip }}</li>
    </ul>

    <!-- The exit is the close cross in the header (App.vue, cptpl_exit). -->
    <SubscribePanel v-if="!cancelled" />
    <div v-else class="cancelled" role="status">
      <p class="cancelled__title">{{ t.cancelledTitle }}</p>
      <p class="cancelled__message">{{ t.cancelledMessage }}</p>
      <button class="btn-cancel" type="button" @click="cancelled = false">{{ t.cancelledCta }}</button>
    </div>

    <p class="compliance cptpl_terms">
      {{ t.compliance.text }}
      <a :href="brandConfig.termsConditionsUrl" target="_blank" rel="noopener noreferrer">{{ t.compliance.link }}</a>
      {{ t.compliance.help }}
    </p>
    </div>

  </section>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore, Steps } from '@/stores/subscriptionStore'
import resolveVariant from '@/functions/variant'
import liveCopy from '@/functions/liveCopy'
import SubscribePanel from '@/components/SubscribePanel.vue'
import PrizeRow from '@/components/PrizeRow.vue'
import PreLander from '@/components/PreLander.vue'

const { brand, brandConfig, translations: t } = storeToRefs(useBrandStore())
const { screen, cancelled, prelanding } = storeToRefs(useSubscriptionStore())
const variant = brandConfig.value.variants[resolveVariant(brandConfig.value)]

const typedNumber = computed(() => screen.value === Steps.Msisdn || screen.value === Steps.Otp)
const hero = computed(() => (typedNumber.value && variant.heroPin) || variant.hero || '')
const subline = computed(() => liveCopy(variant.subline, brandConfig.value.liveStats))
const chips = computed(() => (variant.chips || []).map((chip) => liveCopy(chip, brandConfig.value.liveStats)).filter(Boolean))
// Sizes are specified at the 390px design width (350px column); scale down with the column so narrow
// phones keep the designed line breaks, never above the specified size.
const headlineStyle = computed(() => ({
  fontSize: `min(${variant.headlineSize}px, calc((100vw - 40px) * ${(variant.headlineSize / 350).toFixed(4)}))`,
  ...(variant.headlineLeading ? { lineHeight: variant.headlineLeading } : {})
}))
</script>

<style scoped>
.landing,
.landing__page {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.landing__spacer {
  flex: 1;
  min-height: 24px;
}

/* Art block above the copy; on a short screen it gives way before the copy and the actions do. */
.hero {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex: 0 1 auto;
  min-height: 0;
  padding-bottom: 12px;
}

/* Capped in vh so JOIN / PIN + price + strip stay above the fold on a 360 x 640 screen. */
.hero__img {
  width: 100%;
  max-height: min(236px, 26vh);
  object-fit: contain;
  object-position: center bottom;
  border-radius: var(--radius-md);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}

/* Typed-number steps: the crest alone, smaller, so field + PIN + CONFIRM + strip still fit 360 x 640. */
.hero--compact .hero__img {
  width: auto;
  max-height: min(140px, 19vh);
  border-radius: 0;
  box-shadow: none;
}

.copy {
  display: flex;
  flex-direction: column;
  gap: var(--copy-gap);
}

.copy__eyebrow {
  font-size: var(--eyebrow-size);
  font-weight: var(--eyebrow-weight);
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: var(--eyebrow-tracking);
  color: var(--eyebrow-color);
}

.copy__headline {
  font-family: var(--headline-font);
  font-weight: 700;
  line-height: 1.03;
  letter-spacing: var(--headline-tracking);
  text-transform: var(--headline-transform);
  color: #fff;
}

.copy__subline {
  font-size: var(--subline-size);
  font-weight: 400;
  line-height: 1.5;
  color: var(--subline-color);
}

/* VS Gaming: explicit spacing per element instead of the shared 10px gap. */
.copy--vs-gaming .copy__headline {
  line-height: 1;
}

.copy--vs-gaming .prize + .copy__eyebrow {
  padding-top: 10px;
}

.copy--vs-gaming .copy__eyebrow + .copy__headline {
  padding-top: 4px;
}

.copy--vs-gaming .copy__eyebrow + .prize {
  padding-top: 4px;
}

.copy--vs-gaming .prize + .copy__headline {
  padding-top: 8px;
}

.copy--vs-gaming .copy__eyebrow--chip + .copy__headline {
  padding-top: 14px;
}

.copy--vs-gaming .copy__subline {
  padding-top: 8px;
}

.copy__eyebrow--chip {
  align-self: flex-start;
  padding: 8px 14px;
  border: 1px solid rgba(0, 229, 255, 0.6);
  border-radius: 6px;
  background: rgba(0, 229, 255, 0.08);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: .08em;
  color: var(--chip-cyan);
}

/* Claim strip: lead chip stronger, the rest quieter. */
.chips {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 14px;
}

.chips__chip {
  padding: 7px 11px;
  border: 1px solid var(--chip-border);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: .05em;
  color: rgba(255, 255, 255, 0.85);
}

.chips__chip--lead {
  border: 1.5px solid var(--chip-lead);
  font-size: 12px;
  font-weight: 700;
  color: #fff;
}

.cancelled {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  text-align: center;
}

.cancelled__title {
  font-size: 20px;
  font-weight: 700;
}

.cancelled__message {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
}
</style>
